import React, { useEffect, useState } from 'react';
import {Link, useNavigate} from 'react-router-dom';
import classnames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { Form as AntForm, Input, message, Modal, QRCode, Button } from 'antd';
import { Card, CardBody, CardHeader, Form as ReactstrapForm, Col, Container, Label, Nav, NavItem, NavLink, Row, TabContent, TabPane } from 'reactstrap';
import axios from 'axios';
import api,{handleApiError} from '../../../../helpers/api';

import { useTranslation } from 'react-i18next';
import admin from '../../../../assets/images/users/avatar_admin.png';
import editor from '../../../../assets/images/users/avatar_editor.png';
import support from '../../../../assets/images/users/avatar_support.png';
import {toast} from "react-toastify";

const Settings = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('1');
    const { user } = useSelector(state => ({
        user: state.Login.user,
    }));
    const [User, setUser] = useState(null);
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: ''
    });
    const [is2FAModalVisible, setIs2FAModalVisible] = useState(false);
    const [otpauthUrl, setOtpauthUrl] = useState('');
    const [totpCode, setTotpCode] = useState('');
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
    const [form] = AntForm.useForm();
    const navigate = useNavigate();

    useEffect(() => {
        console.log('User:', user); // Debug log
        if (user) {
            setUser(user);
            setTwoFactorEnabled(user.twoFactorEnabled || false);
        } else {
            const storedUser = localStorage.getItem('user');
            console.log('Stored User:', storedUser); // Debug log
            if (storedUser) {
                try {
                    const parsed = JSON.parse(storedUser);
                    setUser(parsed);
                    setTwoFactorEnabled(parsed.twoFactorEnabled || false);
                } catch (e) {
                    console.error('Failed to parse user:', e);
                }
            }
        }
    }, [user]);

    const getAvatarByRole = (role) => {
        switch(role?.toLowerCase()) {
            case 'admin':
                return admin;
            case 'support':
                return support;
            case 'editor':
                return editor;
            default:
                return admin;
        }
    };

    useEffect(() => {
        if (User) {
            setFormData({
                firstname: User.firstname || '',
                lastname: User.lastname || '',
                role: User.role || '',
            });
        }
    }, [User]);

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleUpdateProfile = async () => {
        try {
            const response = await api.patch('/user/updateMe', {
                firstname: formData.firstname,
                lastname: formData.lastname,
            }, {
                withCredentials: true,
            });
            if (response.data.status === 'success') {
                toast.success(t('settings.messages.profile_updated'));
                const updatedUser = response.data.user;
                setFormData(updatedUser);
                dispatch({
                    type: 'UPDATE_USER',
                    payload: updatedUser,
                });
                localStorage.setItem('user', JSON.stringify(updatedUser));
            } else {
                toast.error(t('settings.messages.profile_update_failed'));
            }
        } catch (error) {
            console.error(error);
            if (error.response.data?.status === 401 || error.response.data?.status === 403) {
                await handleApiError(error, dispatch, navigate);
            } else {
                toast.error(t('settings.messages.server_error'));
            }
        }
    };

    const onFinish = async (values) => {
        try {
            const response = await api.patch('/user/updateMyPassword', {
                currentPassword: values.oldPassword,
                newPassword: values.newPassword,
            }, {
                withCredentials: true,
            });
            if (response.data.status === 200) {
                toast.success(t('settings.messages.password_updated'));
                form.resetFields();
            }
        } catch (error) {
            if (error.response.data?.status === 401 || error.response.data?.status === 403) {
                await handleApiError(error, dispatch, navigate);
            } else {
                toast.error(error.response?.data?.message || t('settings.messages.password_incorrect'));
            }
        }
    };
    const handleEnable2FA = async () => {
        try {
            const response = await api.post('/user/2fa/generate', {}, {
                withCredentials: true,
            });
            console.log('2FA Generate Response:', response.data);
            if (response.data.status === 'success') { // Adjusted to response.data.status
                const otpauthUrl = response.data.data.otpauthUrl;
                console.log('Setting otpauthUrl:', otpauthUrl);
                setOtpauthUrl(otpauthUrl);
                setIs2FAModalVisible(true);
            } else {
                toast.error(t('settings.messages.2fa_generate_failed'));
            }
        } catch (error) {
            console.error('Error generating 2FA:', error);
            if (error.response.data?.status === 401 || error.response.data?.status === 403) {
                await handleApiError(error, dispatch, navigate);
            } else {
                toast.error(error.response?.data?.message || t('settings.messages.2fa_error'));
            }
        }
    };
    const handleDisable2FA = async () => {
        setTotpCode('');
        setIs2FAModalVisible(true);
    };

    const handle2FASubmit = async () => {
        try {
            const endpoint = twoFactorEnabled ? '/user/2fa/disable' : '/user/2fa/verify';
            const response = await api.post(endpoint, { totpCode }, {
                withCredentials: true,
            });
            console.log('2FA Submit Response:', response);
            if (response.data.status === 'success') {
                toast.success(t(twoFactorEnabled ? 'settings.messages.2fa_disabled' : 'settings.messages.2fa_enabled'));
                setTwoFactorEnabled(!twoFactorEnabled);
                const updatedUser = { ...User, twoFactorEnabled: !twoFactorEnabled };
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
                dispatch({
                    type: 'UPDATE_USER',
                    payload: updatedUser,
                });
                setIs2FAModalVisible(false);
                setTotpCode('');
                setOtpauthUrl('');
            } else {
                toast.error(t('settings.messages.2fa_process_failed'));
            }
        } catch (error) {
            console.error('Error processing 2FA:', error);
            if (error.response.data?.status === 401 || error.response.data?.status === 403) {
                await handleApiError(error, dispatch, navigate);
            } else {
                toast.error(error.response?.data?.message || t('settings.messages.server_error'));
            }
        }
    };
    const tabChange = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    document.title = t('settings.title');

    if (!User) {
        return <div>{t('settings.loading_user')}</div>;
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid style={{ marginTop: '48px' }}>
                    <Row>
                        <Col xxl={3}>
                            <Card className="mt-n5">
                                <CardBody className="p-4">
                                    <div className="text-center">
                                        <div className="profile-user position-relative d-inline-block mx-auto mb-4">
                                            <img
                                                src={getAvatarByRole(User?.role)}
                                                className="rounded-circle avatar-xl img-thumbnail user-profile-image"
                                                alt={`Avatar for role: ${User?.role || "Unknown Role"}`}
                                            />
                                        </div>
                                        <h5 className="fs-16 mb-1">{User?.firstname + ' ' + User?.lastname}</h5>
                                        <p className="text-muted mb-0">{User?.role || t('settings.profile.role_unknown')}</p>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col xxl={9}>
                            <Card className="mt-xxl-n5">
                                <CardHeader>
                                    <Nav className="nav-tabs-custom rounded card-header-tabs border-bottom-0" role="tablist">
                                        <NavItem>
                                            <NavLink
                                                className={classnames({ active: activeTab === '1' })}
                                                onClick={() => tabChange('1')}
                                            >
                                                <i className="fas fa-home"></i> {t('settings.tabs.personal_details')}
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                to="#"
                                                className={classnames({ active: activeTab === '2' })}
                                                onClick={() => tabChange('2')}
                                                type="button"
                                            >
                                                <i className="far fa-user"></i> {t('settings.tabs.change_password')}
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                to="#"
                                                className={classnames({ active: activeTab === '4' })}
                                                onClick={() => tabChange('4')}
                                                type="button"
                                            >
                                                <i className="far fa-envelope"></i> {t('settings.tabs.privacy_policy')}
                                            </NavLink>
                                        </NavItem>
                                    </Nav>
                                </CardHeader>
                                <CardBody className="p-4">
                                    <TabContent activeTab={activeTab}>
                                        <TabPane tabId="1">
                                            <ReactstrapForm>
                                                <Row>
                                                    <Col lg={6}>
                                                        <div className="mb-3">
                                                            <Label htmlFor="firstnameInput" className="form-label">
                                                                {t('settings.personal_details.firstname')}
                                                            </Label>
                                                            <Input
                                                                type="text"
                                                                className="form-control"
                                                                id="firstnameInput"
                                                                name="firstname"
                                                                placeholder={t('settings.personal_details.firstname_placeholder')}
                                                                value={formData.firstname}
                                                                onChange={handleChange}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={6}>
                                                        <div className="mb-3">
                                                            <Label htmlFor="lastnameInput" className="form-label">
                                                                {t('settings.personal_details.lastname')}
                                                            </Label>
                                                            <Input
                                                                type="text"
                                                                className="form-control"
                                                                id="lastnameInput"
                                                                name="lastname"
                                                                placeholder={t('settings.personal_details.lastname_placeholder')}
                                                                value={formData.lastname}
                                                                onChange={handleChange}
                                                            />
                                                        </div>
                                                    </Col>
                                                    {/*<Col lg={6}>*/}
                                                    {/*    <div className="mb-3">*/}
                                                    {/*        <Label htmlFor="phonenumberInput" className="form-label">*/}
                                                    {/*            {t('settings.personal_details.phone_number')}*/}
                                                    {/*        </Label>*/}
                                                    {/*        <Input*/}
                                                    {/*            type="text"*/}
                                                    {/*            className="form-control"*/}
                                                    {/*            id="phonenumberInput"*/}
                                                    {/*            placeholder={t('settings.personal_details.phone_number_placeholder')}*/}
                                                    {/*            value={formData.phoneNumber}*/}
                                                    {/*            name="phoneNumber"*/}
                                                    {/*            onChange={handleChange}*/}
                                                    {/*        />*/}
                                                    {/*    </div>*/}
                                                    {/*</Col>*/}
                                                    {/*<Col lg={6}>*/}
                                                    {/*    <div className="mb-3">*/}
                                                    {/*        <Label htmlFor="emailInput" className="form-label">*/}
                                                    {/*            {t('settings.personal_details.email')}*/}
                                                    {/*        </Label>*/}
                                                    {/*        <Input*/}
                                                    {/*            type="email"*/}
                                                    {/*            className="form-control"*/}
                                                    {/*            id="emailInput"*/}
                                                    {/*            placeholder={t('settings.personal_details.email_placeholder')}*/}
                                                    {/*            name="email"*/}
                                                    {/*            value={formData.email}*/}
                                                    {/*            onChange={handleChange}*/}
                                                    {/*        />*/}
                                                    {/*    </div>*/}
                                                    {/*</Col>*/}
                                                    <Col lg={12}>
                                                        <div className="hstack gap-2 justify-content-end">
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary"
                                                                onClick={handleUpdateProfile}
                                                            >
                                                                {t('settings.personal_details.update_button')}
                                                            </button>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </ReactstrapForm>
                                        </TabPane>
                                        <TabPane tabId="2">
                                            <AntForm form={form} onFinish={onFinish} layout="vertical">
                                                <Row className="g-2">
                                                    <Col lg={4}>
                                                        <AntForm.Item
                                                            label={t('settings.change_password.old_password')}
                                                            name="oldPassword"
                                                            rules={[
                                                                { required: true, message: t('settings.change_password.old_password_required') },
                                                                { min: 8, message: t('settings.change_password.password_min_length') },
                                                            ]}
                                                            hasFeedback
                                                        >
                                                            <Input.Password
                                                                className="form-control"
                                                                id="oldpasswordInput"
                                                                placeholder={t('settings.change_password.old_password_placeholder')}
                                                            />
                                                        </AntForm.Item>
                                                    </Col>
                                                    <Col lg={4}>
                                                        <AntForm.Item
                                                            label={t('settings.change_password.new_password')}
                                                            name="newPassword"
                                                            rules={[
                                                                { required: true, message: t('settings.change_password.new_password_required') },
                                                                { min: 8, message: t('settings.change_password.password_min_length') },
                                                            ]}
                                                            hasFeedback
                                                        >
                                                            <Input.Password
                                                                className="form-control"
                                                                id="newpasswordInput"
                                                                placeholder={t('settings.change_password.new_password_placeholder')}
                                                            />
                                                        </AntForm.Item>
                                                    </Col>
                                                    <Col lg={4}>
                                                        <AntForm.Item
                                                            label={t('settings.change_password.confirm_password')}
                                                            name="confirmPassword"
                                                            dependencies={['newPassword']}
                                                            hasFeedback
                                                            rules={[
                                                                { required: true, message: t('settings.change_password.confirm_password_required') },
                                                                ({ getFieldValue }) => ({
                                                                    validator(_, value) {
                                                                        if (!value || getFieldValue('newPassword') === value) {
                                                                            return Promise.resolve();
                                                                        }
                                                                        return Promise.reject(new Error(t('settings.change_password.passwords_mismatch')));
                                                                    },
                                                                }),
                                                            ]}
                                                        >
                                                            <Input.Password
                                                                className="form-control"
                                                                id="confirmpasswordInput"
                                                                placeholder={t('settings.change_password.confirm_password_placeholder')}
                                                            />
                                                        </AntForm.Item>
                                                    </Col>
                                                    <Col lg={12}>
                                                        <div className="text-end">
                                                            <button type="submit" className="btn btn-success">
                                                                {t('settings.change_password.submit_button')}
                                                            </button>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </AntForm>
                                            {/* Commented-out Login History section */}
                                            {/*<div className="mt-4 mb-3 border-bottom pb-2">*/}
                                            {/*    <div className="float-end">*/}
                                            {/*        <Link to="#" className="link-primary">*/}
                                            {/*            {t('settings.login_history.all_logout')}*/}
                                            {/*        </Link>*/}
                                            {/*    </div>*/}
                                            {/*    <h5 className="card-title">{t('settings.login_history.title')}</h5>*/}
                                            {/*</div>*/}
                                            {/*<div className="d-flex align-items-center mb-3">*/}
                                            {/*    <div className="flex-shrink-0 avatar-sm">*/}
                                            {/*        <div className="avatar-title bg-light text-primary rounded-3 fs-18">*/}
                                            {/*            <i className="ri-smartphone-line"></i>*/}
                                            {/*        </div>*/}
                                            {/*    </div>*/}
                                            {/*    <div className="flex-grow-1 ms-3">*/}
                                            {/*        <h6>{t('settings.login_history.iphone')}</h6>*/}
                                            {/*        <p className="text-muted mb-0">{t('settings.login_history.iphone_location')}</p>*/}
                                            {/*    </div>*/}
                                            {/*    <div>*/}
                                            {/*        <Link to="#">{t('settings.login_history.logout')}</Link>*/}
                                            {/*    </div>*/}
                                            {/*</div>*/}
                                            {/*<div className="d-flex align-items-center mb-3">*/}
                                            {/*    <div className="flex-shrink-0 avatar-sm">*/}
                                            {/*        <div className="avatar-title bg-light text-primary rounded-3 fs-18">*/}
                                            {/*            <i className="ri-tablet-line"></i>*/}
                                            {/*        </div>*/}
                                            {/*    </div>*/}
                                            {/*    <div className="flex-grow-1 ms-3">*/}
                                            {/*        <h6>{t('settings.login_history.ipad')}</h6>*/}
                                            {/*        <p className="text-muted mb-0">{t('settings.login_history.ipad_location')}</p>*/}
                                            {/*    </div>*/}
                                            {/*    <div>*/}
                                            {/*        <Link to="#">{t('settings.login_history.logout')}</Link>*/}
                                            {/*    </div>*/}
                                            {/*</div>*/}
                                            {/*<div className="d-flex align-items-center mb-3">*/}
                                            {/*    <div className="flex-shrink-0 avatar-sm">*/}
                                            {/*        <div className="avatar-title bg-light text-primary rounded-3 fs-18">*/}
                                            {/*            <i className="ri-smartphone-line"></i>*/}
                                            {/*        </div>*/}
                                            {/*    </div>*/}
                                            {/*    <div className="flex-grow-1 ms-3">*/}
                                            {/*        <h6>{t('settings.login_history.galaxy')}</h6>*/}
                                            {/*        <p className="text-muted mb-0">{t('settings.login_history.galaxy_location')}</p>*/}
                                            {/*    </div>*/}
                                            {/*    <div>*/}
                                            {/*        <Link to="#">{t('settings.login_history.logout')}</Link>*/}
                                            {/*    </div>*/}
                                            {/*</div>*/}
                                            {/*<div className="d-flex align-items-center">*/}
                                            {/*    <div className="flex-shrink-0 avatar-sm">*/}
                                            {/*        <div className="avatar-title bg-light text-primary rounded-3 fs-18">*/}
                                            {/*            <i className="ri-macbook-line"></i>*/}
                                            {/*        </div>*/}
                                            {/*    </div>*/}
                                            {/*    <div className="flex-grow-1 ms-3">*/}
                                            {/*        <h6>{t('settings.login_history.dell')}</h6>*/}
                                            {/*        <p className="text-muted mb-0">{t('settings.login_history.dell_location')}</p>*/}
                                            {/*    </div>*/}
                                            {/*    <div>*/}
                                            {/*        <Link to="#">{t('settings.login_history.logout')}</Link>*/}
                                            {/*    </div>*/}
                                            {/*</div>*/}
                                        </TabPane>
                                        <TabPane tabId="4">
                                            <div className="mb-4 pb-2">
                                                <h5 className="card-title text-decoration-underline mb-3">{t('settings.privacy_policy.security')}</h5>
                                                <div className="d-flex flex-column flex-sm-row mb-4 mb-sm-0">
                                                    <div className="flex-grow-1">
                                                        <h6 className="fs-14 mb-1">{t('settings.privacy_policy.two_factor_title')}</h6>
                                                        <p className="text-muted">
                                                            {t('settings.privacy_policy.two_factor_description')}
                                                        </p>
                                                    </div>
                                                    <div className="flex-shrink-0 ms-sm-3">
                                                        <Button
                                                            type="primary"
                                                            size="small"
                                                            onClick={twoFactorEnabled ? handleDisable2FA : handleEnable2FA}
                                                        >
                                                            {t(twoFactorEnabled ? 'settings.privacy_policy.disable_2fa' : 'settings.privacy_policy.enable_2fa')}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </TabPane>
                                    </TabContent>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
            <Modal
                title={t(twoFactorEnabled ? 'settings.2fa_modal.disable_title' : 'settings.2fa_modal.enable_title')}
                open={is2FAModalVisible}
                onCancel={() => {
                    setIs2FAModalVisible(false);
                    setTotpCode('');
                    setOtpauthUrl('');
                }}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setIs2FAModalVisible(false);
                        setTotpCode('');
                        setOtpauthUrl('');
                    }}>
                        {t('settings.2fa_modal.cancel')}
                    </Button>,
                    <Button key="submit" type="primary" onClick={handle2FASubmit}>
                        {t(twoFactorEnabled ? 'settings.2fa_modal.disable' : 'settings.2fa_modal.enable')}
                    </Button>,
                ]}
            >
                {!twoFactorEnabled && otpauthUrl && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        marginBottom: '20px',
                    }}>
                        <p style={{ textAlign: 'center' }}>{t('settings.2fa_modal.qr_instruction')}</p>
                        <QRCode value={otpauthUrl} size={200} />
                    </div>
                )}
                <Input
                    placeholder={t('settings.2fa_modal.code_placeholder')}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    maxLength={6}
                    style={{ marginBottom: '20px' }}
                />
            </Modal>
        </React.Fragment>
    );
};

export default Settings;